'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { CreatorMultiSelect } from '@/components/shared/creator-multi-select';
import type { SortPillOption } from '@/components/shared/sort-pill-row';
import { AnimatedButton } from '@/components/ui/animated-button';
import { ChipMultiSelect } from '@/components/ui/chip-multi-select';
import { DateRangeSlider } from '@/components/ui/date-range-slider';
import { type DateRangeLabels, formatDateRangeValueLabel } from '@/components/ui/date-range-slider-format';
import { Icon } from '@/components/ui/icon';
import { Cancel01Icon, FilterVerticalIcon } from '@/components/ui/icon/glyphs';
import { RangeSlider } from '@/components/ui/range-slider';
import { type MultiSelectOption, SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SegmentedControl, type SegmentedControlItem } from '@/components/ui/segmented-control';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { formatRangeValueLabel, type RangeFormatSpec } from '@/lib/currency';
import { MOTION_TAP } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
const FILTER_TRIGGER_TAP = {
  y: 0,
  scale: 0.97,
  transition: MOTION_TAP
} as const;

// ---------------------------------------------------------------------------
// Dimension model — a `type`-discriminated union. Each variant declares its
// own value shape; `FilterContent` switches on `type` to render the matching
// primitive, and the active-chip bar summarises selections per-variant.
//
// `searchableSelect` is the legacy single-select shape; a dimension with no
// `type` is treated as `searchableSelect` so existing callers (markets,
// proposals) keep working unchanged.
// ---------------------------------------------------------------------------

interface SelectFilterDimension {
  type?: 'searchableSelect';
  key: string;
  label: string;
  placeholder: string;
  allLabel: string;
  options: Array<{
    value: string;
    label: string;
  }>;
}
interface SliderFilterDimension {
  type: 'slider';
  key: string;
  label: string;
  min: number;
  max: number;
  step?: number;
  format: RangeFormatSpec;
}
interface DateSliderFilterDimension {
  type: 'dateSlider';
  key: string;
  label: string;
  spanDays: number;
  now?: number;
  labels?: DateRangeLabels;
}
interface ChipsMultiFilterDimension {
  type: 'chipsMulti';
  key: string;
  label: string;
  options: Array<{
    value: string;
    label: string;
    emoji?: string;
  }>;
}
interface SearchMultiFilterDimension {
  type: 'searchMulti';
  key: string;
  label: string;
  placeholder: string;
  options?: MultiSelectOption[];
  onSearch?: (query: string) => Promise<MultiSelectOption[]>;
  emptyLabel?: string;
}
interface CreatorMultiFilterDimension {
  type: 'creatorMulti';
  key: string;
  label: string;
  placeholder: string;
  emptyLabel: string;
}
export type FilterDimension = SelectFilterDimension | SliderFilterDimension | DateSliderFilterDimension | ChipsMultiFilterDimension | SearchMultiFilterDimension | CreatorMultiFilterDimension;

/**
 * Per-key filter value. The shape depends on the dimension type:
 *  - searchableSelect → `string | null`
 *  - slider           → `[number, number]` (native units)
 *  - dateSlider       → `[number, number]` (percent positions, 0–100)
 *  - chipsMulti       → `string[]`
 *  - searchMulti      → `MultiSelectOption[]`
 */
export type FilterValue = string | null | [number, number] | string[] | MultiSelectOption[];
interface ActiveFilter {
  key: string;
  label: string;
  /** When set, clicking the chip clears this single member rather than the whole dimension. */
  memberValue?: string;
}
interface SortFilterBarProps {
  /** Sort pill options (e.g. Trending, Newest). */
  sortOptions: SortPillOption[];
  activeSort: string;
  onSortChange: (sort: string) => void;
  /** Filter dimensions shown inside the sheet/dialog. */
  filters: FilterDimension[];
  /** Current filter values keyed by dimension key. */
  activeFilters: Record<string, FilterValue | undefined>;
  onFilterChange: (key: string, value: FilterValue) => void;
  onClearAll: () => void;
  /** Label for the filter sheet/dialog title. */
  filterSheetTitle: string;
  /** Label for the "Clear all" action. */
  clearAllLabel: string;
  /** Label for the filter trigger button (sr-only). */
  filterButtonLabel: string;
  /** Footer "Reset" button label. When both this and `applyLabel` are set, the
   *  sheet renders a sticky Reset + apply footer (matches the filter mockup). */
  resetLabel?: string;
  /** Footer primary action label, e.g. "Show ~74 markets" (consumer composes
   *  the count + noun for i18n). Closes the sheet — filters apply live. */
  applyLabel?: string;
  /** Accessible label for the header close button (i18n). */
  closeLabel?: string;
  /** Format an "N selected" count for chip-multi dimensions. */
  selectedCountLabel?: (count: number) => string;
  /** Analytics callback when sort changes. */
  onSortEvent?: (value: string, previous: string) => void;
  /** Optional cache warm for a sort pill before its selection is committed. */
  onSortIntent?: (value: string) => void;
  /** Optional extra content rendered below filters in the sheet/dialog. */
  /** Optional content rendered ABOVE the filter sections (e.g. a "My X" toggle). */
  topContent?: React.ReactNode | undefined;
  /** Optional extra content rendered BELOW the filter sections. */
  extraContent?: React.ReactNode | undefined;
  /** Optional element before the filter/settings control (e.g. global search). */
  leadingToolbarAction?: React.ReactNode;
  /** Optional trailing element in the sort pills row (e.g. watchlist button). */
  trailingAction?: React.ReactNode;
  /** External filter-modal open state (lifted from parent). Falls back to internal state when absent. */
  filterOpen?: boolean;
  /** Callback when filter-modal open state changes. Must be provided alongside `filterOpen`. */
  onFilterOpenChange?: (open: boolean) => void;
  /** When true, hides the inline settings/filter button (it's rendered externally). */
  hideInlineSettings?: boolean;
}

// ---------------------------------------------------------------------------
// Type-narrowing accessors — read a value as the shape its dimension expects.
// ---------------------------------------------------------------------------

function asSelectValue(value: FilterValue | undefined): string | null {
  return typeof value === 'string' ? value : null;
}
function asRange(value: FilterValue | undefined, fallback: [number, number]): [number, number] {
  if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'number') {
    return [value[0], value[1] as number];
  }
  return fallback;
}
function asStringArray(value: FilterValue | undefined): string[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')) {
    return value as string[];
  }
  return [];
}
function asMultiOptions(value: FilterValue | undefined): MultiSelectOption[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'object')) {
    return value as MultiSelectOption[];
  }
  return [];
}
function dimensionType(dim: FilterDimension): NonNullable<FilterDimension['type']> {
  return dim.type ?? 'searchableSelect';
}

/** The "no selection" value for a dimension — used when clearing the draft. */
function clearedValue(dim: FilterDimension): FilterValue {
  switch (dimensionType(dim)) {
    case 'slider':
      {
        const slider = dim as SliderFilterDimension;
        return [slider.min, slider.max];
      }
    case 'dateSlider':
      return [0, 100];
    case 'chipsMulti':
    case 'searchMulti':
    case 'creatorMulti':
      return [];
    default:
      return null;
  }
}

/** Structural equality for filter values (primitives, ranges, multi-options). */
function filterValueEqual(a: FilterValue | undefined, b: FilterValue | undefined): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((x, i): boolean => {
      const y = b[i];
      if (typeof x === 'object' && x !== null && typeof y === 'object' && y !== null) {
        return (x as MultiSelectOption).value === (y as MultiSelectOption).value;
      }
      return x === y;
    });
  }
  return false;
}

// ---------------------------------------------------------------------------
// Active-chip summary — one entry per active dimension (multis fan out to one
// chip per member). Each entry knows how to clear itself.
// ---------------------------------------------------------------------------

function buildActiveChips(filters: FilterDimension[], activeFilters: Record<string, FilterValue | undefined>, selectedCountLabel: (count: number) => string): ActiveFilter[] {
  return filters.flatMap((dim): ActiveFilter[] => {
    const value = activeFilters[dim.key];
    switch (dimensionType(dim)) {
      case 'searchableSelect':
        {
          const select = dim as SelectFilterDimension;
          const v = asSelectValue(value);
          if (v === null) return [];
          const opt = select.options.find(o => o.value === v);
          if (opt === undefined) return [];
          return [{
            key: dim.key,
            label: opt.label
          }];
        }
      case 'slider':
        {
          const slider = dim as SliderFilterDimension;
          const [lo, hi] = asRange(value, [slider.min, slider.max]);
          if (lo <= slider.min && hi >= slider.max) return [];
          const summary = formatRangeValueLabel(lo, hi, slider.min, slider.max, slider.format);
          return [{
            key: dim.key,
            label: `${slider.label}: ${summary}`
          }];
        }
      case 'dateSlider':
        {
          const date = dim as DateSliderFilterDimension;
          const [lo, hi] = asRange(value, [0, 100]);
          if (lo <= 0 && hi >= 100) return [];
          const summary = formatDateRangeValueLabel(lo, hi, date.spanDays, date.now ?? Date.now(), date.labels);
          return [{
            key: dim.key,
            label: `${date.label}: ${summary}`
          }];
        }
      case 'chipsMulti':
        {
          const chips = dim as ChipsMultiFilterDimension;
          const values = asStringArray(value);
          if (values.length === 0) return [];
          if (values.length > 2) {
            return [{
              key: dim.key,
              label: `${chips.label}: ${selectedCountLabel(values.length)}`
            }];
          }
          return values.flatMap((v): ActiveFilter[] => {
            const opt = chips.options.find(o => o.value === v);
            if (opt === undefined) return [];
            const prefix = opt.emoji !== undefined ? `${opt.emoji} ` : '';
            return [{
              key: dim.key,
              label: `${prefix}${opt.label}`,
              memberValue: v
            }];
          });
        }
      case 'searchMulti':
      case 'creatorMulti':
        {
          const selected = asMultiOptions(value);
          if (selected.length === 0) return [];
          return selected.map((opt): ActiveFilter => {
            const prefix = opt.emoji !== undefined ? `${opt.emoji} ` : '';
            return {
              key: dim.key,
              label: `${prefix}${opt.label}`,
              memberValue: opt.value
            };
          });
        }
      default:
        return [];
    }
  });
}

// ---------------------------------------------------------------------------
// Filter content — dispatches each dimension to its primitive.
// ---------------------------------------------------------------------------

function FilterContent(t0) {
  const $ = _c(11);
  const {
    filters,
    activeFilters,
    onFilterChange,
    selectedCountLabel
  } = t0;
  let t1;
  if ($[0] !== activeFilters || $[1] !== filters || $[2] !== onFilterChange || $[3] !== selectedCountLabel) {
    let t2;
    if ($[5] !== activeFilters || $[6] !== onFilterChange || $[7] !== selectedCountLabel) {
      t2 = dim => <div key={dim.key} className="flex flex-col gap-2 border-border-secondary border-b py-4 first:pt-0 last:border-b-0 last:pb-0"><span className="text-body font-medium text-foreground-primary">{dim.label}</span><FilterControl dim={dim} value={activeFilters[dim.key]} onChange={v => onFilterChange(dim.key, v)} selectedCountLabel={selectedCountLabel} /></div>;
      $[5] = activeFilters;
      $[6] = onFilterChange;
      $[7] = selectedCountLabel;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = filters.map(t2);
    $[0] = activeFilters;
    $[1] = filters;
    $[2] = onFilterChange;
    $[3] = selectedCountLabel;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <div className="flex flex-col">{t1}</div>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function FilterControl(t0) {
  const $ = _c(54);
  const {
    dim,
    value,
    onChange,
    selectedCountLabel
  } = t0;
  switch (dimensionType(dim)) {
    case "slider":
      {
        const slider = dim as SliderFilterDimension;
        const t1 = slider.label;
        const t2 = slider.min;
        const t3 = slider.max;
        const t4 = slider.step;
        let t5;
        if ($[0] !== slider.max || $[1] !== slider.min || $[2] !== value) {
          t5 = asRange(value, [slider.min, slider.max]);
          $[0] = slider.max;
          $[1] = slider.min;
          $[2] = value;
          $[3] = t5;
        } else {
          t5 = $[3];
        }
        let t6;
        if ($[4] !== onChange || $[5] !== slider.format || $[6] !== slider.label || $[7] !== slider.max || $[8] !== slider.min || $[9] !== slider.step || $[10] !== t5) {
          t6 = <RangeSlider label={t1} min={t2} max={t3} step={t4} value={t5} onChange={onChange} format={slider.format} />;
          $[4] = onChange;
          $[5] = slider.format;
          $[6] = slider.label;
          $[7] = slider.max;
          $[8] = slider.min;
          $[9] = slider.step;
          $[10] = t5;
          $[11] = t6;
        } else {
          t6 = $[11];
        }
        return t6;
      }
    case "dateSlider":
      {
        const date = dim as DateSliderFilterDimension;
        const t1 = date.label;
        const t2 = date.spanDays;
        const t3 = date.now;
        const t4 = date.labels;
        let t5;
        if ($[12] !== value) {
          t5 = asRange(value, [0, 100]);
          $[12] = value;
          $[13] = t5;
        } else {
          t5 = $[13];
        }
        let t6;
        if ($[14] !== date.label || $[15] !== date.labels || $[16] !== date.now || $[17] !== date.spanDays || $[18] !== onChange || $[19] !== t5) {
          t6 = <DateRangeSlider label={t1} spanDays={t2} now={t3} labels={t4} value={t5} onChange={onChange} />;
          $[14] = date.label;
          $[15] = date.labels;
          $[16] = date.now;
          $[17] = date.spanDays;
          $[18] = onChange;
          $[19] = t5;
          $[20] = t6;
        } else {
          t6 = $[20];
        }
        return t6;
      }
    case "chipsMulti":
      {
        const chips = dim as ChipsMultiFilterDimension;
        const t1 = chips.options;
        let t2;
        if ($[21] !== value) {
          t2 = asStringArray(value);
          $[21] = value;
          $[22] = t2;
        } else {
          t2 = $[22];
        }
        let t3;
        if ($[23] !== chips.options || $[24] !== onChange || $[25] !== selectedCountLabel || $[26] !== t2) {
          t3 = <ChipMultiSelect options={t1} value={t2} onChange={onChange} renderCount={selectedCountLabel} />;
          $[23] = chips.options;
          $[24] = onChange;
          $[25] = selectedCountLabel;
          $[26] = t2;
          $[27] = t3;
        } else {
          t3 = $[27];
        }
        return t3;
      }
    case "searchMulti":
      {
        const search = dim as SearchMultiFilterDimension;
        let t1;
        if ($[28] !== value) {
          t1 = asMultiOptions(value);
          $[28] = value;
          $[29] = t1;
        } else {
          t1 = $[29];
        }
        let t2;
        if ($[30] !== onChange || $[31] !== search.emptyLabel || $[32] !== search.onSearch || $[33] !== search.options || $[34] !== search.placeholder || $[35] !== t1) {
          t2 = <SearchableMultiSelect value={t1} onChange={onChange} placeholder={search.placeholder} options={search.options} onSearch={search.onSearch} emptyLabel={search.emptyLabel} />;
          $[30] = onChange;
          $[31] = search.emptyLabel;
          $[32] = search.onSearch;
          $[33] = search.options;
          $[34] = search.placeholder;
          $[35] = t1;
          $[36] = t2;
        } else {
          t2 = $[36];
        }
        return t2;
      }
    case "creatorMulti":
      {
        const creator = dim as CreatorMultiFilterDimension;
        let t1;
        if ($[37] !== value) {
          t1 = asMultiOptions(value);
          $[37] = value;
          $[38] = t1;
        } else {
          t1 = $[38];
        }
        let t2;
        if ($[39] !== creator.emptyLabel || $[40] !== creator.placeholder || $[41] !== onChange || $[42] !== t1) {
          t2 = <CreatorMultiSelect value={t1} onChange={onChange} placeholder={creator.placeholder} emptyLabel={creator.emptyLabel} />;
          $[39] = creator.emptyLabel;
          $[40] = creator.placeholder;
          $[41] = onChange;
          $[42] = t1;
          $[43] = t2;
        } else {
          t2 = $[43];
        }
        return t2;
      }
    default:
      {
        const select = dim as SelectFilterDimension;
        const t1 = select.options;
        let t2;
        if ($[44] !== value) {
          t2 = asSelectValue(value);
          $[44] = value;
          $[45] = t2;
        } else {
          t2 = $[45];
        }
        let t3;
        if ($[46] !== onChange) {
          t3 = v => onChange(v);
          $[46] = onChange;
          $[47] = t3;
        } else {
          t3 = $[47];
        }
        let t4;
        if ($[48] !== select.allLabel || $[49] !== select.options || $[50] !== select.placeholder || $[51] !== t2 || $[52] !== t3) {
          t4 = <SearchableSelect options={t1} value={t2} onValueChange={t3} placeholder={select.placeholder} allLabel={select.allLabel} className="w-full h-10" />;
          $[48] = select.allLabel;
          $[49] = select.options;
          $[50] = select.placeholder;
          $[51] = t2;
          $[52] = t3;
          $[53] = t4;
        } else {
          t4 = $[53];
        }
        return t4;
      }
  }
}

// ---------------------------------------------------------------------------
// Toolbar row — sort segmented control + optional leading/trailing actions and
// the inline filter trigger button.
// ---------------------------------------------------------------------------

function SortFilterToolbar(t0) {
  const $ = _c(28);
  const {
    sortItems,
    activeSort,
    onSortClick,
    onSortIntent,
    leadingToolbarAction,
    trailingAction,
    toolbarEndFlat,
    hideInlineSettings,
    filterCount,
    filterButtonLabel,
    onOpenFilters
  } = t0;
  const prefersReducedMotion = useReducedMotion();
  let t1;
  if ($[0] !== onSortIntent) {
    t1 = onSortIntent !== undefined ? {
      onItemPointerEnter: onSortIntent
    } : {};
    $[0] = onSortIntent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activeSort || $[3] !== onSortClick || $[4] !== sortItems || $[5] !== t1) {
    t2 = <SegmentedControl variant="tab" asTabs={true} items={sortItems} active={activeSort} onChange={onSortClick} {...t1} layoutId="sort-active-pill" />;
    $[2] = activeSort;
    $[3] = onSortClick;
    $[4] = sortItems;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== leadingToolbarAction || $[8] !== trailingAction) {
    t3 = (trailingAction !== undefined || leadingToolbarAction !== undefined) && <div className="hidden lg:block lg:flex-1 min-w-0" />;
    $[7] = leadingToolbarAction;
    $[8] = trailingAction;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== toolbarEndFlat) {
    t4 = toolbarEndFlat ? "contents" : cn("flex shrink-0 items-center gap-2 max-lg:ml-auto lg:ml-0");
    $[10] = toolbarEndFlat;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== filterButtonLabel || $[13] !== filterCount || $[14] !== hideInlineSettings || $[15] !== onOpenFilters || $[16] !== prefersReducedMotion || $[17] !== toolbarEndFlat) {
    t5 = hideInlineSettings !== true && <m.button type="button" {...prefersReducedMotion ? {} : {
      whileTap: FILTER_TRIGGER_TAP
    }} transition={MOTION_TAP} className={cn("relative inline-flex size-8 shrink-0 items-center justify-center rounded-full p-0 text-body", filterCount > 0 ? "bg-background-brand text-foreground-brand hover:bg-background-brand/90 dark:bg-background-primary dark:text-foreground-primary dark:hover:bg-background-primary/90" : "bg-background-tertiary text-foreground-secondary hover:bg-tertiary hover:text-foreground-primary", toolbarEndFlat ? "max-lg:ml-auto" : false)} onClick={onOpenFilters} aria-label={filterButtonLabel}><Icon icon={FilterVerticalIcon} className="size-3.5" />{filterCount > 0 && <span className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-background-primary px-0.5 text-foreground-primary text-micro font-medium leading-none">{filterCount}</span>}</m.button>;
    $[12] = filterButtonLabel;
    $[13] = filterCount;
    $[14] = hideInlineSettings;
    $[15] = onOpenFilters;
    $[16] = prefersReducedMotion;
    $[17] = toolbarEndFlat;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== leadingToolbarAction || $[20] !== t4 || $[21] !== t5 || $[22] !== trailingAction) {
    t6 = <div className={t4}>{leadingToolbarAction}{t5}{trailingAction}</div>;
    $[19] = leadingToolbarAction;
    $[20] = t4;
    $[21] = t5;
    $[22] = trailingAction;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t2 || $[25] !== t3 || $[26] !== t6) {
    t7 = <LazyMotion features={domAnimation}><div className="flex min-w-0 gap-0 overflow-x-auto overflow-y-visible scrollbar-none">{t2}{t3}{t6}</div></LazyMotion>;
    $[24] = t2;
    $[25] = t3;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
}

// ---------------------------------------------------------------------------
// Filter sheet — the SilkSheet modal holding the filter sections, header
// (title + clear/close), and optional sticky reset/apply footer.
// ---------------------------------------------------------------------------

function FilterSheet(t0) {
  const $ = _c(35);
  const {
    open,
    onOpenChange,
    filterSheetTitle,
    clearAllLabel,
    closeLabel,
    resetLabel,
    applyLabel,
    onClearAll,
    onApply,
    filters,
    displayValues,
    onContentChange,
    selectedCountLabel,
    topContent,
    extraContent
  } = t0;
  let t1;
  if ($[0] !== filterSheetTitle) {
    t1 = <h2 className="text-heading-md text-foreground-primary">{filterSheetTitle}</h2>;
    $[0] = filterSheetTitle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== clearAllLabel || $[3] !== onClearAll) {
    t2 = <AnimatedButton variant="tertiary" size="sm" className="text-foreground-brand hover:text-foreground-brand" onClick={onClearAll}>{clearAllLabel}</AnimatedButton>;
    $[2] = clearAllLabel;
    $[3] = onClearAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onOpenChange) {
    t3 = () => onOpenChange(false);
    $[5] = onOpenChange;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== closeLabel || $[8] !== t3) {
    t4 = <AnimatedButton variant="tertiary" size="sm" prefix={Cancel01Icon} aria-label={closeLabel} onClick={t3} />;
    $[7] = closeLabel;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className="flex items-center gap-1">{t2}{t4}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t1 || $[14] !== t5) {
    t6 = <div className="flex items-center justify-between gap-3">{t1}{t5}</div>;
    $[13] = t1;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== applyLabel || $[17] !== onApply || $[18] !== onClearAll || $[19] !== resetLabel) {
    t7 = applyLabel !== undefined ? <div className="flex items-center gap-2">{resetLabel !== undefined && <AnimatedButton variant="tertiary" size="default" onClick={onClearAll}>{resetLabel}</AnimatedButton>}<AnimatedButton variant="primary" size="default" className="flex-1" onClick={onApply}>{applyLabel}</AnimatedButton></div> : undefined;
    $[16] = applyLabel;
    $[17] = onApply;
    $[18] = onClearAll;
    $[19] = resetLabel;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = topContent !== undefined && topContent;
  let t9;
  if ($[21] !== displayValues || $[22] !== filters || $[23] !== onContentChange || $[24] !== selectedCountLabel) {
    t9 = <FilterContent filters={filters} activeFilters={displayValues} onFilterChange={onContentChange} selectedCountLabel={selectedCountLabel} />;
    $[21] = displayValues;
    $[22] = filters;
    $[23] = onContentChange;
    $[24] = selectedCountLabel;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = extraContent !== undefined && extraContent;
  let t11;
  if ($[26] !== filterSheetTitle || $[27] !== onOpenChange || $[28] !== open || $[29] !== t10 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t11 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" size="default" contentClassName="overflow-x-hidden" sheetClassName="max-h-[88dvh]" disableDesktopSwipe={true} keyboard={true} closeButton={false} header={t6} title={filterSheetTitle} footer={t7}>{t8}{t9}{t10}</SilkSheet>;
    $[26] = filterSheetTitle;
    $[27] = onOpenChange;
    $[28] = open;
    $[29] = t10;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}

// ---------------------------------------------------------------------------
// Active-chip bar — one removable chip per active dimension/member, plus a
// text-only "clear all" affordance when 2+ dimensions are active.
// ---------------------------------------------------------------------------

function ActiveChipsBar(t0) {
  const $ = _c(12);
  const {
    activeChips,
    filterCount,
    clearAllLabel,
    onClearChip,
    onClearAll
  } = t0;
  let t1;
  if ($[0] !== activeChips || $[1] !== onClearChip) {
    let t2;
    if ($[3] !== onClearChip) {
      t2 = chip => <button key={`${chip.key}:${chip.memberValue ?? ""}`} type="button" className="flex items-center gap-1 shrink-0 rounded-full border border-border-primary bg-background-tertiary px-2.5 py-1 text-body text-foreground-primary transition-colors hover:bg-background-tertiary" onClick={() => onClearChip(chip)}><span>{chip.label}</span><Icon icon={Cancel01Icon} className="size-3 text-foreground-secondary" /></button>;
      $[3] = onClearChip;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = activeChips.map(t2);
    $[0] = activeChips;
    $[1] = onClearChip;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== clearAllLabel || $[6] !== filterCount || $[7] !== onClearAll) {
    t2 = filterCount >= 2 && <button type="button" className="shrink-0 rounded-full px-2.5 py-1 text-body text-foreground-secondary hover:text-foreground-primary transition-colors" onClick={onClearAll}>{clearAllLabel}</button>;
    $[5] = clearAllLabel;
    $[6] = filterCount;
    $[7] = onClearAll;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <div className="flex gap-1.5 overflow-x-auto scrollbar-none">{t1}{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SortFilterBar(t0) {
  const $ = _c(78);
  const {
    sortOptions,
    activeSort,
    onSortChange,
    filters,
    activeFilters,
    onFilterChange,
    onClearAll,
    filterSheetTitle,
    clearAllLabel,
    filterButtonLabel,
    resetLabel,
    applyLabel,
    closeLabel,
    selectedCountLabel,
    onSortEvent,
    onSortIntent,
    topContent,
    extraContent,
    leadingToolbarAction,
    trailingAction,
    filterOpen: externalOpen,
    onFilterOpenChange: externalOnOpenChange,
    hideInlineSettings
  } = t0;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  let t1;
  if ($[0] !== externalOnOpenChange) {
    t1 = function setOpen(value) {
      if (externalOnOpenChange !== undefined) {
        externalOnOpenChange(value);
      } else {
        setInternalOpen(value);
      }
    };
    $[0] = externalOnOpenChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setOpen = t1;
  const deferred = applyLabel !== undefined;
  const [draft, setDraft] = useState(activeFilters);
  const prevOpen = useRef(false);
  let t2;
  let t3;
  if ($[2] !== activeFilters || $[3] !== deferred || $[4] !== open) {
    t2 = () => {
      if (open && !prevOpen.current && deferred) {
        setDraft({
          ...activeFilters
        });
      }
      prevOpen.current = open;
    };
    t3 = [open, deferred, activeFilters];
    $[2] = activeFilters;
    $[3] = deferred;
    $[4] = open;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const displayValues = deferred ? draft : activeFilters;
  let t4;
  if ($[7] !== deferred || $[8] !== onFilterChange) {
    t4 = function handleContentChange(key, value_0) {
      if (deferred) {
        setDraft(d => ({
          ...d,
          [key]: value_0
        }));
      } else {
        onFilterChange(key, value_0);
      }
    };
    $[7] = deferred;
    $[8] = onFilterChange;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleContentChange = t4;
  const countLabel = selectedCountLabel ?? _temp;
  let activeChips;
  let t5;
  if ($[10] !== activeFilters || $[11] !== countLabel || $[12] !== filters) {
    activeChips = buildActiveChips(filters, activeFilters, countLabel);
    t5 = new Set(activeChips.map(_temp2));
    $[10] = activeFilters;
    $[11] = countLabel;
    $[12] = filters;
    $[13] = activeChips;
    $[14] = t5;
  } else {
    activeChips = $[13];
    t5 = $[14];
  }
  const filterCount = t5.size;
  let t6;
  if ($[15] !== activeSort || $[16] !== onSortChange || $[17] !== onSortEvent) {
    t6 = function handleSortClick(value_1) {
      if (value_1 !== activeSort) {
        onSortEvent?.(value_1, activeSort);
        onSortChange(value_1);
      }
    };
    $[15] = activeSort;
    $[16] = onSortChange;
    $[17] = onSortEvent;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleSortClick = t6;
  let t7;
  if ($[19] !== activeFilters || $[20] !== filters || $[21] !== onFilterChange) {
    t7 = function clearDimension(chip) {
      const dim = filters.find(d_0 => d_0.key === chip.key);
      if (dim === undefined) {
        return;
      }
      switch (dimensionType(dim)) {
        case "slider":
          {
            const slider = dim as SliderFilterDimension;
            onFilterChange(chip.key, [slider.min, slider.max]);
            return;
          }
        case "dateSlider":
          {
            onFilterChange(chip.key, [0, 100]);
            return;
          }
        case "chipsMulti":
          {
            if (chip.memberValue !== undefined) {
              const current_0 = asStringArray(activeFilters[chip.key]);
              onFilterChange(chip.key, current_0.filter(v_0 => v_0 !== chip.memberValue));
            } else {
              onFilterChange(chip.key, []);
            }
            return;
          }
        case "searchMulti":
        case "creatorMulti":
          {
            if (chip.memberValue !== undefined) {
              const current = asMultiOptions(activeFilters[chip.key]);
              onFilterChange(chip.key, current.filter(v => v.value !== chip.memberValue));
            } else {
              onFilterChange(chip.key, []);
            }
            return;
          }
        default:
          {
            onFilterChange(chip.key, null);
          }
      }
    };
    $[19] = activeFilters;
    $[20] = filters;
    $[21] = onFilterChange;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const clearDimension = t7;
  let t8;
  if ($[23] !== deferred || $[24] !== filters || $[25] !== onClearAll) {
    t8 = function handleClearAll() {
      if (deferred) {
        const cleared = {};
        for (const dim_0 of filters) {
          cleared[dim_0.key] = clearedValue(dim_0);
        }
        setDraft(cleared);
      }
      onClearAll();
    };
    $[23] = deferred;
    $[24] = filters;
    $[25] = onClearAll;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const handleClearAll = t8;
  let t9;
  if ($[27] !== activeFilters || $[28] !== deferred || $[29] !== draft || $[30] !== filters || $[31] !== onFilterChange || $[32] !== setOpen) {
    t9 = function handleApply() {
      if (deferred) {
        for (const dim_1 of filters) {
          const next = draft[dim_1.key] ?? clearedValue(dim_1);
          if (!filterValueEqual(next, activeFilters[dim_1.key])) {
            onFilterChange(dim_1.key, next);
          }
        }
      }
      setOpen(false);
    };
    $[27] = activeFilters;
    $[28] = deferred;
    $[29] = draft;
    $[30] = filters;
    $[31] = onFilterChange;
    $[32] = setOpen;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleApply = t9;
  let t10;
  if ($[34] !== sortOptions) {
    t10 = sortOptions.map(_temp3);
    $[34] = sortOptions;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const sortItems = t10;
  const toolbarEndFlat = trailingAction === undefined && leadingToolbarAction === undefined;
  let t11;
  if ($[36] !== setOpen) {
    t11 = () => setOpen(true);
    $[36] = setOpen;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== activeSort || $[39] !== filterButtonLabel || $[40] !== filterCount || $[41] !== handleSortClick || $[42] !== hideInlineSettings || $[43] !== leadingToolbarAction || $[44] !== onSortIntent || $[45] !== sortItems || $[46] !== t11 || $[47] !== toolbarEndFlat || $[48] !== trailingAction) {
    t12 = <SortFilterToolbar sortItems={sortItems} activeSort={activeSort} onSortClick={handleSortClick} onSortIntent={onSortIntent} leadingToolbarAction={leadingToolbarAction} trailingAction={trailingAction} toolbarEndFlat={toolbarEndFlat} hideInlineSettings={hideInlineSettings} filterCount={filterCount} filterButtonLabel={filterButtonLabel} onOpenFilters={t11} />;
    $[38] = activeSort;
    $[39] = filterButtonLabel;
    $[40] = filterCount;
    $[41] = handleSortClick;
    $[42] = hideInlineSettings;
    $[43] = leadingToolbarAction;
    $[44] = onSortIntent;
    $[45] = sortItems;
    $[46] = t11;
    $[47] = toolbarEndFlat;
    $[48] = trailingAction;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== applyLabel || $[51] !== clearAllLabel || $[52] !== closeLabel || $[53] !== countLabel || $[54] !== displayValues || $[55] !== extraContent || $[56] !== filterSheetTitle || $[57] !== filters || $[58] !== handleApply || $[59] !== handleClearAll || $[60] !== handleContentChange || $[61] !== open || $[62] !== resetLabel || $[63] !== setOpen || $[64] !== topContent) {
    t13 = <FilterSheet open={open} onOpenChange={setOpen} filterSheetTitle={filterSheetTitle} clearAllLabel={clearAllLabel} closeLabel={closeLabel} resetLabel={resetLabel} applyLabel={applyLabel} onClearAll={handleClearAll} onApply={handleApply} filters={filters} displayValues={displayValues} onContentChange={handleContentChange} selectedCountLabel={countLabel} topContent={topContent} extraContent={extraContent} />;
    $[50] = applyLabel;
    $[51] = clearAllLabel;
    $[52] = closeLabel;
    $[53] = countLabel;
    $[54] = displayValues;
    $[55] = extraContent;
    $[56] = filterSheetTitle;
    $[57] = filters;
    $[58] = handleApply;
    $[59] = handleClearAll;
    $[60] = handleContentChange;
    $[61] = open;
    $[62] = resetLabel;
    $[63] = setOpen;
    $[64] = topContent;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[66] !== t12 || $[67] !== t13) {
    t14 = <div className="flex min-w-0 flex-col">{t12}{t13}</div>;
    $[66] = t12;
    $[67] = t13;
    $[68] = t14;
  } else {
    t14 = $[68];
  }
  let t15;
  if ($[69] !== activeChips || $[70] !== clearAllLabel || $[71] !== clearDimension || $[72] !== filterCount || $[73] !== onClearAll) {
    t15 = activeChips.length > 0 && <ActiveChipsBar activeChips={activeChips} filterCount={filterCount} clearAllLabel={clearAllLabel} onClearChip={clearDimension} onClearAll={onClearAll} />;
    $[69] = activeChips;
    $[70] = clearAllLabel;
    $[71] = clearDimension;
    $[72] = filterCount;
    $[73] = onClearAll;
    $[74] = t15;
  } else {
    t15 = $[74];
  }
  let t16;
  if ($[75] !== t14 || $[76] !== t15) {
    t16 = <div className="flex flex-col gap-2">{t14}{t15}</div>;
    $[75] = t14;
    $[76] = t15;
    $[77] = t16;
  } else {
    t16 = $[77];
  }
  return t16;
}
function _temp3(option) {
  return {
    key: option.value,
    label: option.label,
    ...(option.prefix !== undefined ? {
      icon: option.prefix
    } : {})
  };
}
function _temp2(c) {
  return c.key;
}
function _temp(count) {
  return String(count);
}